export type EmployeeLeaveType = "CASUAL" | "SICK" | "UNPAID" | "OTHER";

export type EmployeeLeaveRequestStatus = "PENDING" | "APPROVED" | "DECLINED" | "CANCELLED";

export type EmployeeLeaveRequestSource = "ADMIN" | "WHATSAPP";

export const LEAVE_TYPE_CHOICES: { id: EmployeeLeaveType; name: string }[] = [
  { id: "CASUAL", name: "Casual" },
  { id: "SICK", name: "Sick" },
  { id: "UNPAID", name: "Unpaid" },
  { id: "OTHER", name: "Other" },
];

export const LEAVE_STATUS_CHOICES: { id: EmployeeLeaveRequestStatus; name: string }[] = [
  { id: "PENDING", name: "Pending" },
  { id: "APPROVED", name: "Approved" },
  { id: "DECLINED", name: "Declined" },
  { id: "CANCELLED", name: "Cancelled" },
];

export function leaveTypeLabel(type: string | null | undefined): string {
  if (!type) return "—";
  const found = LEAVE_TYPE_CHOICES.find((c) => c.id === type);
  return found?.name ?? type;
}

export function leaveStatusChipColor(
  status: string | undefined
): "default" | "success" | "error" | "warning" | "info" {
  switch (status) {
    case "APPROVED":
      return "success";
    case "DECLINED":
      return "error";
    case "CANCELLED":
      return "default";
    case "PENDING":
      return "warning";
    default:
      return "default";
  }
}

/** Normalize react-admin / MUI date values to YYYY-MM-DD for the leave API. */
export function toLeaveIsoDate(value: unknown): string | null {
  if (value == null || value === "") return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }
  return null;
}

export type LeaveCreateFormValues = {
  employeeId?: number | string;
  leaveType?: EmployeeLeaveType;
  startDate?: unknown;
  endDate?: unknown;
  reason?: string | null;
};

/** Transform create form → API body. */
export function transformLeaveCreatePayload(data: LeaveCreateFormValues): {
  employeeId: number;
  leaveType: EmployeeLeaveType;
  startDate: string;
  endDate: string;
  reason?: string | null;
} {
  const employeeId = Number(data.employeeId);
  if (!Number.isFinite(employeeId) || employeeId <= 0) {
    throw new Error("Employee is required");
  }
  const startDate = toLeaveIsoDate(data.startDate);
  const endDate = toLeaveIsoDate(data.endDate);
  if (!startDate || !endDate) {
    throw new Error("Start and end dates are required (YYYY-MM-DD)");
  }
  if (!data.leaveType) {
    throw new Error("Leave type is required");
  }
  return {
    employeeId,
    leaveType: data.leaveType,
    startDate,
    endDate,
    reason: data.reason?.trim() ? data.reason.trim() : null,
  };
}

export type LeaveHistoryMode = "office" | "self";

export function leaveCreatePath(employeeId?: number | string | null): string {
  const base = "/employeeLeaveRequests/create";
  if (employeeId == null || employeeId === "") return base;
  return `${base}?employeeId=${encodeURIComponent(String(employeeId))}`;
}

export function leaveShowPath(id: string): string {
  return `/employeeLeaveRequests/${id}/show`;
}

/** API path for leave list: office inbox vs self `/me`. */
export function leaveListApiLocation(mode: LeaveHistoryMode): string {
  return mode === "self" ? "employeeLeaveRequests/me" : "employeeLeaveRequests";
}

/**
 * Create href for leave history actions.
 * Self mode uses an in-page dialog (null); office uses the create resource route.
 */
export function leaveHistoryCreateHref(
  mode: LeaveHistoryMode,
  employeeId?: number | string | null
): string | null {
  if (mode === "self") return null;
  return leaveCreatePath(employeeId);
}

/** Show path is shared so WhatsApp / deep links hit the hardened leave show. */
export function leaveHistoryShowPath(_mode: LeaveHistoryMode, id: string): string {
  return leaveShowPath(id);
}

/** Portal-only personal hub: myAttendance without office employees.view. */
export function isPortalOnlyLeaveAccess(canOfficeView: boolean, canMyAttendance: boolean): boolean {
  return canMyAttendance && !canOfficeView;
}

/** Leave inbox sidebar: office managers only (portal users use My dashboard). */
export function shouldShowLeaveRequestsInMenu(canOfficeView: boolean): boolean {
  return canOfficeView;
}

export type MyLeaveCreateFormValues = {
  leaveType?: EmployeeLeaveType;
  startDate?: unknown;
  endDate?: unknown;
  reason?: string | null;
};

/** Transform self-serve create form → POST …/me body (no employeeId). */
export function transformMyLeaveCreatePayload(data: MyLeaveCreateFormValues): {
  leaveType: EmployeeLeaveType;
  startDate: string;
  endDate: string;
  reason?: string | null;
} {
  const startDate = toLeaveIsoDate(data.startDate);
  const endDate = toLeaveIsoDate(data.endDate);
  if (!startDate || !endDate) {
    throw new Error("Start and end dates are required (YYYY-MM-DD)");
  }
  if (!data.leaveType) {
    throw new Error("Leave type is required");
  }
  return {
    leaveType: data.leaveType,
    startDate,
    endDate,
    reason: data.reason?.trim() ? data.reason.trim() : null,
  };
}

/** Prefer Nest `message` from axios errors; fall back to Error.message. */
export function leaveApiErrorMessage(error: unknown, fallback = "Request failed"): string {
  if (error && typeof error === "object") {
    const ax = error as {
      response?: { data?: { message?: string | string[] } };
      message?: string;
    };
    const apiMsg = ax.response?.data?.message;
    if (Array.isArray(apiMsg) && apiMsg.length > 0) {
      return apiMsg.join(", ");
    }
    if (typeof apiMsg === "string" && apiMsg.trim()) {
      return apiMsg;
    }
    if (typeof ax.message === "string" && ax.message.trim()) {
      return ax.message;
    }
  }
  return fallback;
}

/**
 * Autocomplete → employees getList filter.
 * Employees API has no `q` query field; use JSON `filter` with Prisma contains.
 */
export function employeeNameFilterToQuery(searchText: string): Record<string, string> {
  const q = searchText.trim();
  if (!q) return {};
  return {
    filter: JSON.stringify({
      name: { contains: q, mode: "insensitive" },
    }),
  };
}

export type LeaveReviewAction = "approve" | "decline" | "cancel";

export type LeaveReviewCallParams = {
  location: string;
  method: "PUT";
  id?: string;
  data: { reviewRemarks: string | null };
};

/** Build PUT params for approve / decline / cancel (office vs portal self-cancel). */
export function buildLeaveReviewCall(
  kind: LeaveReviewAction,
  leaveId: string,
  remarks: string,
  portalOnly: boolean
): LeaveReviewCallParams {
  const reviewRemarks = remarks.trim() ? remarks.trim() : null;
  if (portalOnly && kind === "cancel") {
    return {
      location: `employeeLeaveRequests/me/${leaveId}/cancel`,
      method: "PUT",
      data: { reviewRemarks },
    };
  }
  return {
    location: `employeeLeaveRequests/${kind}`,
    method: "PUT",
    id: leaveId,
    data: { reviewRemarks },
  };
}

/** Display approver remarks with a consistent empty placeholder. */
export function formatLeaveApproverRemarks(
  value: string | null | undefined,
  options?: { empty?: string }
): string {
  const empty = options?.empty ?? "—";
  if (value == null || String(value).trim() === "") return empty;
  return String(value).trim();
}

/** Truncate remarks for list / table cells (~40 chars). */
export function truncateLeaveRemarks(value: string | null | undefined, maxLen = 40): string {
  const text = formatLeaveApproverRemarks(value, { empty: "" });
  if (!text) return "—";
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen - 1)}…`;
}

export function isDecidedLeaveStatus(status: string | undefined): boolean {
  return status === "APPROVED" || status === "DECLINED" || status === "CANCELLED";
}

/** Staff-facing vs office copy for approver remarks labels. */
export function leaveApproverRemarksLabel(portalOnly: boolean): string {
  return portalOnly ? "Approver remarks" : "Remarks";
}

/** Extract updated leave row from approve/decline/cancel API `{ rows: [...] }` body. */
export function extractLeaveReviewRow(responseBody: unknown): Record<string, unknown> | null {
  const body = responseBody as { rows?: unknown[] };
  const rows = Array.isArray(body?.rows) ? body.rows : null;
  if (!rows || rows.length === 0) return null;
  const row = rows[0];
  return row && typeof row === "object" ? (row as Record<string, unknown>) : null;
}

/** True when approve/decline hits a non-PENDING row (double-submit or stale UI). */
export function isLeaveAlreadyProcessedError(error: unknown): boolean {
  if (error && typeof error === "object") {
    const ax = error as { response?: { status?: number; data?: { message?: string } } };
    if (ax.response?.status === 400 && ax.response?.data?.message === "Request already processed") {
      return true;
    }
  }
  return leaveApiErrorMessage(error, "") === "Request already processed";
}

export type LeavePendingAdjustment = {
  startDate: string;
  endDate: string;
  reason?: string | null;
  requestedAt?: string | null;
  requestedVia?: string | null;
};

export type LeaveAdjustmentAction = "approve" | "decline";

const DAY_MS = 24 * 60 * 60 * 1000;

function isoDayToMs(iso: string): number {
  return Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
}

function msToIsoDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

export type LeaveAdjustmentImpact = {
  /** Days the leave no longer covers (shortened). */
  removed: string[];
  /** Days newly covered (extended). */
  added: string[];
};

/** Calendar days covered by exactly one of the old / new inclusive ranges. */
export function computeLeaveAdjustmentImpact(
  oldStart: string,
  oldEnd: string,
  newStart: string,
  newEnd: string
): LeaveAdjustmentImpact {
  const os = isoDayToMs(oldStart);
  const oe = isoDayToMs(oldEnd);
  const ns = isoDayToMs(newStart);
  const ne = isoDayToMs(newEnd);
  const impact: LeaveAdjustmentImpact = { removed: [], added: [] };
  if ([os, oe, ns, ne].some(Number.isNaN)) return impact;
  for (let t = Math.min(os, ns); t <= Math.max(oe, ne); t += DAY_MS) {
    const inOld = t >= os && t <= oe;
    const inNew = t >= ns && t <= ne;
    if (inOld && !inNew) impact.removed.push(msToIsoDay(t));
    if (!inOld && inNew) impact.added.push(msToIsoDay(t));
  }
  return impact;
}

/** Drop Sundays (the default weekly off) from `YYYY-MM-DD` day keys; holidays are not known client-side. */
export function excludeSundays(days: string[]): string[] {
  return days.filter((d) => new Date(`${d}T00:00:00Z`).getUTCDay() !== 0);
}

/** GET params for the working days (Sundays + office holidays excluded) in a range. */
export function buildLeaveWorkingDaysCall(startDate: string, endDate: string) {
  return {
    location: "employeeLeaveRequests/working-days",
    method: "GET" as const,
    data: { startDate, endDate },
  };
}

/** Parse `{ days: [...] }` from the working-days endpoint; null when the payload is unusable. */
export function parseWorkingDaysPayload(data: unknown): string[] | null {
  const days = (data as { days?: unknown } | null)?.days;
  if (!Array.isArray(days) || !days.every((d) => typeof d === "string")) return null;
  return days as string[];
}

/** Keep only the impact days that are working days. */
export function filterImpactToWorkingDays(
  impact: LeaveAdjustmentImpact,
  workingDays: readonly string[]
): LeaveAdjustmentImpact {
  const set = new Set(workingDays);
  return {
    removed: impact.removed.filter((d) => set.has(d)),
    added: impact.added.filter((d) => set.has(d)),
  };
}

/** Client-side check before PUT adjust; the API re-validates (overlap, payroll lock, balance). */
export function validateLeaveAdjustment(input: {
  oldStart: string;
  oldEnd: string;
  newStart: string | null;
  newEnd: string | null;
  reason: string;
}): string | null {
  if (!input.newStart || !input.newEnd) return "Start and end dates are required";
  if (input.newStart > input.newEnd) return "Start date must be on or before end date";
  if (
    input.newStart === input.oldStart.slice(0, 10) &&
    input.newEnd === input.oldEnd.slice(0, 10)
  ) {
    return "New dates are the same as the approved dates";
  }
  if (!input.reason.trim()) return "A reason is required";
  return null;
}

/** Whether the "Adjust dates" action applies: approved leave with nothing already awaiting approval. */
export function canAdjustLeave(record: {
  status?: string;
  pendingAdjustment?: LeavePendingAdjustment | null;
}): boolean {
  return record.status === "APPROVED" && !record.pendingAdjustment;
}

export type LeaveAdjustCallParams = {
  location: string;
  method: "PUT";
  id?: string;
  data: { startDate: string; endDate: string; reason: string };
};

/** PUT params to ask for a date adjustment (office vs portal self). */
export function buildLeaveAdjustCall(
  leaveId: string,
  dates: { startDate: string; endDate: string },
  reason: string,
  portalOnly: boolean
): LeaveAdjustCallParams {
  const data = { ...dates, reason: reason.trim() };
  if (portalOnly) {
    return { location: `employeeLeaveRequests/me/${leaveId}/adjust`, method: "PUT", data };
  }
  return { location: "employeeLeaveRequests/adjust", method: "PUT", id: leaveId, data };
}

/** PUT params for approving / declining a pending adjustment (office only). */
export function buildLeaveAdjustmentReviewCall(
  kind: LeaveAdjustmentAction,
  leaveId: string,
  remarks: string
): LeaveReviewCallParams {
  return {
    location: `employeeLeaveRequests/adjust/${kind}`,
    method: "PUT",
    id: leaveId,
    data: { reviewRemarks: remarks.trim() ? remarks.trim() : null },
  };
}
